/**
 * Org Leaderboard Screen — Phase 2D
 *
 * Route: /org-leaderboard?orgId=<uuid>
 *
 * Displays the ranked leaderboard for a given organization, scoped via
 * organization_memberships (not gyms/home_gym_id).
 *
 * orgId can be:
 *   1. Passed as a route param ?orgId=<uuid>  (from org-roster, club-setup)
 *   2. Resolved via resolveClubOwnerOrg(userId) if not passed (owner context)
 *
 * Membership scoping: only status='active' members appear.
 * Excluded: inactive, invited, claim_pending.
 *
 * Doctrine: DUEL_CLUB_MODE_ARCHITECTURE.md
 *   - organizations table canonical — no gym/home_gym_id dependency.
 *   - Does not modify the gym leaderboard path.
 */

import React, { useCallback, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ActivityIndicator,
  TouchableOpacity,
  FlatList,
  SafeAreaView,
} from 'react-native';
import { StatusBar } from 'expo-status-bar';
import { useRouter, useLocalSearchParams, useFocusEffect } from 'expo-router';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { fetchLeaderboardOrgV1 } from '@/lib/leaderboard';
import { resolveClubOwnerOrg } from '@/lib/orgProvisioning';
import { fetchOrganization } from '@/lib/organizations';
import { TYPO } from '@/constants/typography';
import type { OrgLeaderboardEntry, Organization } from '@/types/database';

// ─── Role badge labels ────────────────────────────────────────────────────────

const ROLE_LABELS: Record<string, string> = {
  owner: 'Owner',
  admin: 'Admin',
  coach: 'Coach',
  member: '',
};

// ─── Row component ────────────────────────────────────────────────────────────

function OrgLeaderboardRow({
  entry,
  isCurrentUser,
  c,
}: {
  entry: OrgLeaderboardEntry;
  isCurrentUser: boolean;
  c: any;
}) {
  const rankLabel = entry.rank != null ? `#${entry.rank}` : '—';
  const scoreLabel =
    entry.strength_score != null && isFinite(entry.strength_score)
      ? Math.round(entry.strength_score).toString()
      : '—';
  const name = entry.display_name || entry.handle || 'Unknown';
  const handleLabel = entry.handle ? `@${entry.handle}` : null;
  const roleBadge = ROLE_LABELS[entry.org_role] ?? '';

  return (
    <View
      style={[
        styles.row,
        { backgroundColor: isCurrentUser ? c.accent + '14' : c.surface, borderColor: c.border },
      ]}
    >
      {/* Rank */}
      <Text style={[styles.rankText, { color: c.textTertiary }]}>{rankLabel}</Text>

      {/* Name / handle */}
      <View style={styles.rowCenter}>
        <Text style={[styles.nameText, { color: c.text }]} numberOfLines={1}>
          {name}
        </Text>
        {handleLabel ? (
          <Text style={[styles.handleText, { color: c.textSecondary }]} numberOfLines={1}>
            {handleLabel}
          </Text>
        ) : null}
      </View>

      {/* Right: role badge + score */}
      <View style={styles.rowRight}>
        {roleBadge ? (
          <View style={[styles.rolePill, { backgroundColor: c.accent + '18' }]}>
            <Text style={[styles.rolePillText, { color: c.accent }]}>{roleBadge}</Text>
          </View>
        ) : null}
        <Text style={[styles.scoreText, { color: c.text }]}>{scoreLabel}</Text>
      </View>
    </View>
  );
}

// ─── Screen ───────────────────────────────────────────────────────────────────

export default function OrgLeaderboardScreen() {
  const router = useRouter();
  const { orgId: paramOrgId } = useLocalSearchParams<{ orgId?: string }>();
  const { session } = useAuth();
  const { theme } = useTheme();
  const c = theme.colors;

  const userId = session?.user?.id ?? '';

  const [resolvedOrgId, setResolvedOrgId] = useState<string | null>(paramOrgId ?? null);
  const [org, setOrg] = useState<Organization | null>(null);
  const [rows, setRows] = useState<OrgLeaderboardEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    setError(null);

    try {
      // Resolve org id: prefer route param, fall back to owner org resolution
      let orgId = paramOrgId ?? resolvedOrgId;
      if (!orgId) {
        orgId = await resolveClubOwnerOrg(userId);
        if (orgId) setResolvedOrgId(orgId);
      }

      if (!orgId) {
        setError('No organization found. Make sure you are a member of an organization.');
        setLoading(false);
        return;
      }

      // Load org name for header
      const fetchedOrg = await fetchOrganization(orgId);
      setOrg(fetchedOrg);

      // Fetch leaderboard
      const entries = await fetchLeaderboardOrgV1(orgId);
      setRows(entries);
    } catch (err: any) {
      setError('Failed to load leaderboard. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [userId, paramOrgId, resolvedOrgId]);

  useFocusEffect(
    useCallback(() => {
      load();
    }, [load]),
  );

  // ── Render ────────────────────────────────────────────────────────────────

  const orgName = org?.name ?? 'Organization';

  return (
    <SafeAreaView style={[styles.container, { backgroundColor: c.background }]}>
      <StatusBar style="light" />

      {/* Header */}
      <View style={[styles.header, { borderBottomColor: c.border }]}>
        <TouchableOpacity
          style={[styles.backButton, { borderColor: c.border }]}
          onPress={() => {
            const orgIdForNav = resolvedOrgId ?? paramOrgId;
            if (orgIdForNav) {
              router.push({
                pathname: '/club-home' as any,
                params: { orgId: orgIdForNav },
              });
            } else {
              router.back();
            }
          }}
        >
          <Text style={[styles.backButtonText, { color: c.text }]}>← Club</Text>
        </TouchableOpacity>
        <View style={styles.headerCenter}>
          <Text style={[styles.headerTitle, { color: c.text }]} numberOfLines={1}>
            {orgName}
          </Text>
          <Text style={[styles.headerSub, { color: c.textSecondary }]}>Leaderboard</Text>
        </View>
      </View>

      {/* Body */}
      {loading ? (
        <View style={styles.center}>
          <ActivityIndicator size="large" color={c.accent} />
          <Text style={[styles.subtleText, { color: c.textTertiary }]}>Loading leaderboard…</Text>
        </View>
      ) : error ? (
        <View style={styles.center}>
          <Text style={[styles.errorText, { color: c.textSecondary }]}>{error}</Text>
          <TouchableOpacity
            style={[styles.retryButton, { backgroundColor: c.surfaceVariant }]}
            onPress={load}
          >
            <Text style={[styles.retryText, { color: c.text }]}>Retry</Text>
          </TouchableOpacity>
        </View>
      ) : rows.length === 0 ? (
        <View style={styles.center}>
          <Text style={[styles.emptyTitle, { color: c.text }]}>No ranked members yet.</Text>
          <Text style={[styles.emptyText, { color: c.textSecondary }]}>
            Invite members and complete workouts to appear here.
          </Text>
        </View>
      ) : (
        <>
          {/* Column headers */}
          <View style={[styles.colHeader, { borderBottomColor: c.border }]}>
            <Text style={[styles.colHeaderRank, { color: c.textTertiary }]}>Rank</Text>
            <Text style={[styles.colHeaderName, { color: c.textTertiary }]}>Athlete</Text>
            <Text style={[styles.colHeaderScore, { color: c.textTertiary }]}>Score</Text>
          </View>

          <FlatList
            data={rows}
            keyExtractor={(item) => item.user_id}
            renderItem={({ item }) => (
              <OrgLeaderboardRow
                entry={item}
                isCurrentUser={item.user_id === userId}
                c={c}
              />
            )}
            contentContainerStyle={styles.listContent}
          />
        </>
      )}
    </SafeAreaView>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: { flex: 1 },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    gap: 12,
  },
  backButton: {
    paddingVertical: 6,
    paddingHorizontal: 12,
    borderRadius: 2,
    borderWidth: 1,
  },
  backButtonText: { fontFamily: TYPO.body.semibold, fontSize: 14 },
  headerCenter: { flex: 1 },
  headerTitle: {
    fontSize: 17,
    fontFamily: TYPO.heading.bold,
  },
  headerSub: {
    fontSize: 12,
    fontFamily: TYPO.body.regular,
    marginTop: 1,
  },
  center: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 32,
    gap: 12,
  },
  subtleText: { fontSize: 14, marginTop: 8 },
  errorText: { fontSize: 14, textAlign: 'center' },
  retryButton: { paddingHorizontal: 20, paddingVertical: 10, borderRadius: 4 },
  retryText: { fontFamily: TYPO.body.semibold, fontSize: 14 },
  emptyTitle: {
    fontSize: 17,
    fontFamily: TYPO.heading.bold,
    textAlign: 'center',
  },
  emptyText: {
    fontSize: 14,
    textAlign: 'center',
    lineHeight: 20,
  },
  colHeader: {
    flexDirection: 'row',
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderBottomWidth: 1,
  },
  colHeaderRank: { width: 44, fontSize: 11, fontFamily: TYPO.body.semibold, textTransform: 'uppercase' },
  colHeaderName: { flex: 1, fontSize: 11, fontFamily: TYPO.body.semibold, textTransform: 'uppercase' },
  colHeaderScore: { width: 60, fontSize: 11, fontFamily: TYPO.body.semibold, textAlign: 'right', textTransform: 'uppercase' },
  listContent: { paddingHorizontal: 12, paddingVertical: 8, gap: 6 },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: 6,
    borderWidth: 1,
    paddingVertical: 10,
    paddingHorizontal: 12,
    gap: 10,
  },
  rankText: {
    width: 32,
    fontSize: 14,
    fontFamily: TYPO.body.semibold,
    textAlign: 'center',
  },
  rowCenter: { flex: 1 },
  nameText: { fontSize: 15, fontFamily: TYPO.body.semibold },
  handleText: { fontSize: 12, marginTop: 2 },
  rowRight: {
    alignItems: 'flex-end',
    gap: 4,
    minWidth: 60,
  },
  rolePill: {
    borderRadius: 4,
    paddingHorizontal: 6,
    paddingVertical: 2,
  },
  rolePillText: { fontSize: 10, fontFamily: TYPO.body.semibold },
  scoreText: {
    fontSize: 15,
    fontFamily: TYPO.heading.bold,
    textAlign: 'right',
  },
});
